import { useNavigation } from '@app/hooks/useNavigation';
import { Spinner } from '@components/ui';
import { useScanResultQuery } from '@dieterApi/scan/useScanResultQuery';
import { translateScannerNote } from '@app/utils/translateScannerNote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PrintIcon from '@mui/icons-material/Print';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import {
  SECURITY_HEADER_HELP_KEY,
  computeCoverage,
  getDisplaySiteTitle,
  parseDseMatches,
} from '@app/routes/Dashboard/Topic/SubSections/TechnologyScan/scanResultShared';

const fullPath = '/scan-result-report/:propertyId';

interface RouteParams {
  propertyId?: string;
}

interface CookieState {
  name: string;
  domain: string;
}

interface ConsentState {
  cookies: CookieState[];
  third_parties: { domain: string }[];
}

interface ParsedResults {
  status?: string;
  site?: {
    privacy_score?: string;
    https_ok?: boolean;
    dse_found_url?: string;
    dse_matches?: unknown;
  };
  states?: {
    pre_consent?: Partial<ConsentState> | null;
    post_accept?: Partial<ConsentState> | null;
    post_reject?: Partial<ConsentState> | null;
  };
}

// ── Score styles ──────────────────────────────────────────────────────────────

const SCORE_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  A: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300' },
  B: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-300' },
  C: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-300' },
  D: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-300' },
  E: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-300' },
  F: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-400' },
};

// ── Small helpers ─────────────────────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-lg font-semibold text-primary-800 border-b-2 border-primary-root pb-2 mb-4 mt-8 first:mt-0">
      {children}
    </h2>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-4 ${className}`}>
      {children}
    </div>
  );
}

function StatusBadge({ variant, children }: { variant: 'ok' | 'warn' | 'error' | 'neutral'; children: React.ReactNode }) {
  const cls =
    variant === 'ok'
      ? 'bg-green-50 text-green-700 border-green-200'
      : variant === 'warn'
        ? 'bg-yellow-50 text-orange-600 border-yellow-200'
        : variant === 'error'
          ? 'bg-red-50 text-red-700 border-red-200'
          : 'bg-gray-50 text-gray-600 border-gray-200';
  return (
    <span className={`inline-block text-xs font-semibold border rounded-full px-2.5 py-0.5 whitespace-nowrap ${cls}`}>
      {children}
    </span>
  );
}

function SeverityBlock({
  severity,
  title,
  description,
  items,
}: {
  severity: 'critical' | 'important' | 'recommended';
  title: string;
  description: string;
  items: string[];
}) {
  if (items.length === 0) return null;

  const colors =
    severity === 'critical'
      ? { border: 'border-red-400', bg: 'bg-red-50', titleColor: 'text-red-700', iconBg: 'bg-red-500 text-white', icon: '!' }
      : severity === 'important'
        ? { border: 'border-orange-400', bg: 'bg-orange-50', titleColor: 'text-orange-700', iconBg: 'bg-orange-500 text-white', icon: '▲' }
        : { border: 'border-blue-300', bg: 'bg-blue-50', titleColor: 'text-blue-700', iconBg: 'bg-blue-500 text-white', icon: '→' };

  return (
    <div className={`rounded-xl border-2 ${colors.border} ${colors.bg} p-4`}>
      <div className="flex items-center gap-3 mb-2">
        <span className={`rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 ${colors.iconBg}`}>
          {colors.icon}
        </span>
        <h3 className={`font-semibold text-sm ${colors.titleColor}`}>{title}</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">{description}</p>
      <ul className="space-y-2.5">
        {items.map((item, i) => (
          <li key={i} className="text-sm text-gray-700 leading-snug flex gap-2">
            <span className="flex-shrink-0 mt-0.5 text-gray-400">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ScanResultReport() {
  const { t, i18n } = useTranslation();
  const match = useRouteMatch<RouteParams>(fullPath);
  const propertyId = match?.params?.propertyId;
  const [fetchScanResult, { data, loading, error }] = useScanResultQuery();
  const { navigation, setNavigation } = useNavigation();

  useEffect(() => {
    if (!propertyId) return;
    void fetchScanResult({ variables: { propertyId }, fetchPolicy: 'network-only' });
  }, [propertyId, fetchScanResult]);

  // Set print view so nav/header is hidden
  useEffect(() => {
    if (navigation.isPrintView) return;
    setNavigation((nav) => void (nav.isPrintView = true));
  }, [navigation, setNavigation]);

  useEffect(() => {
    return () => {
      setNavigation((nav) => void (nav.isPrintView = false));
    };
  }, [setNavigation]);

  if (!propertyId) {
    return (
      <div className="p-6">
        <p className="text-sm text-gray-600">{t('route.scanresult.report.missing_id')}</p>
      </div>
    );
  }

  const scanResult = data?.getScanResult;
  const websiteTitle = scanResult?.startUrl ? getDisplaySiteTitle(scanResult.startUrl) : t('route.scanresult.report.fallback_title');
  const scanDateLabel = scanResult?.createdAt
    ? new Date(scanResult.createdAt).toLocaleDateString(i18n.resolvedLanguage || i18n.language || undefined)
    : null;

  // ── Parse results ──────────────────────────────────────────────────────────

  let parsed: ParsedResults | null = null;
  try {
    parsed = scanResult?.results ? (JSON.parse(scanResult.results) as ParsedResults) : null;
  } catch {
    parsed = null;
  }

  const site = parsed?.site;
  const score = site?.privacy_score?.toUpperCase() ?? null;
  const scoreStyle = score ? SCORE_STYLES[score] : undefined;

  // Normalize the consent states up front: aborted or partial scans may lack a
  // sub-state or its arrays, and every render block below may then index freely.
  const normalizeState = (s?: Partial<ConsentState> | null): ConsentState => ({
    cookies: Array.isArray(s?.cookies) ? s.cookies.filter((c) => !!c?.name) : [],
    third_parties: Array.isArray(s?.third_parties) ? s.third_parties.filter((tp) => !!tp?.domain) : [],
  });
  const states = parsed?.states
    ? {
        pre_consent: normalizeState(parsed.states.pre_consent),
        post_accept: normalizeState(parsed.states.post_accept),
        post_reject: normalizeState(parsed.states.post_reject),
      }
    : undefined;

  // DSE matches + coverage (shared with ScanResultDisplay and the score tooltip)
  const dseMatches = parseDseMatches(site?.dse_matches);

  const securityHeaders = dseMatches.security_headers ?? null;
  const scanProfile = dseMatches.scan_profile;

  const {
    dseTpDomains,
    technicallyNecessaryTpDomains,
    dseCookieNames,
    genericCoveredCookieNames: genericCoveredNames,
    technicallyNecessaryCookieNames: technicallyNecessaryNames,
    allCookieNames,
    allTpDomains,
    cookiesNotInDse,
    tpsNotInDse,
    totalDeductions,
  } = computeCoverage(dseMatches, states);

  // Cookies set before consent that are not technically necessary
  const cookiesBeforeConsent = (states?.pre_consent.cookies ?? []).filter(
    (c) => !technicallyNecessaryNames.has(c.name.toLowerCase()),
  );

  // Third-party domains contacted before consent: deduplicated, and only those
  // NOT already in the DSE and NOT technically necessary (disclosed services are
  // a separate nuanced debate; technically-necessary static-asset CDNs don't
  // belong in a privacy policy and don't deduct from the score).
  const tpsBeforeConsentUnique: { domain: string }[] = [];
  const seenTpDomains = new Set<string>();
  for (const tp of states?.pre_consent?.third_parties ?? []) {
    const d = tp.domain.toLowerCase();
    if (!seenTpDomains.has(d) && !dseTpDomains.has(d) && !technicallyNecessaryTpDomains.has(d)) {
      seenTpDomains.add(d);
      tpsBeforeConsentUnique.push(tp);
    }
  }

  const hasConsentViolations = cookiesBeforeConsent.length > 0 || tpsBeforeConsentUnique.length > 0;

  const dseFound = !!(scanResult?.dpsUrl || site?.dse_found_url);

  // ── Build action plan ──────────────────────────────────────────────────────

  const criticalItems: string[] = [];
  const importantItems: string[] = [];
  const recommendedItems: string[] = [];

  // Critical: cookies and/or third parties before consent (TTDSG §25)
  if (hasConsentViolations) {
    // Build the subject clause from only the categories that actually occurred,
    // so the text never claims "0 cookie(s)" and correctly states that the
    // counted third parties are the ones NOT disclosed in the DSE (matching the
    // tpsBeforeConsentUnique filter above).
    const subjectParts: string[] = [];
    if (cookiesBeforeConsent.length > 0) {
      subjectParts.push(
        t('route.scanresult.report.action_plan.consent_violation_cookies_clause', {
          count: cookiesBeforeConsent.length,
          names: cookiesBeforeConsent.map((c) => c.name).join(', '),
        }),
      );
    }
    if (tpsBeforeConsentUnique.length > 0) {
      subjectParts.push(
        t('route.scanresult.report.action_plan.consent_violation_tps_clause', {
          count: tpsBeforeConsentUnique.length,
        }),
      );
    }
    criticalItems.push(
      t('route.scanresult.report.action_plan.consent_violation_action', {
        subject: subjectParts.join(t('route.scanresult.report.action_plan.consent_violation_and')),
      }),
    );
  }

  // Critical: no DSE at all
  if (!dseFound) {
    criticalItems.push(t('route.scanresult.report.action_plan.no_dse_action'));
  }

  // Critical: CSP completely missing is a security best practice, not a legal
  // requirement under the GDPR — it is not abmahnfähig. Route it to the
  // "recommended" block alongside CSP 'warn' so the critical block stays
  // reserved for genuine legal risks (consent violations, missing DSE).
  // CSP 'warn' (present but with weak directives) is also recommended.
  // For warn-rated headers, prefer a _warn_action key if defined, falling back to _missing_action.
  // Important: cookies missing in DSE
  if (cookiesNotInDse.length > 0) {
    importantItems.push(t('route.scanresult.report.action_plan.cookies_missing_action', { count: cookiesNotInDse.length }));
  }
  // Important: third parties missing in DSE
  if (tpsNotInDse.length > 0) {
    importantItems.push(t('route.scanresult.report.action_plan.tps_missing_action', { count: tpsNotInDse.length }));
  }

  securityHeaders?.headers?.forEach((hdr) => {
    const key = SECURITY_HEADER_HELP_KEY[hdr.name];
    if (!key) return;
    if (hdr.rating === 'bad' || hdr.rating === 'warn') {
      const warnKey = `route.scanresult.report.action_plan.${key}_warn_action`;
      const badKey = `route.scanresult.report.action_plan.${key}_missing_action`;
      recommendedItems.push(
        hdr.rating === 'warn' ? t([warnKey, badKey] as unknown as string) : t(badKey),
      );
    }
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#FCF8F2] print:bg-white">
      <div className="max-w-4xl mx-auto px-4 py-8 print:px-0 print:py-4">

        {/* ── Page header ── */}
        <div className="flex items-start justify-between gap-4 mb-6 print:mb-4">
          <div>
            <h1 className="text-2xl font-bold text-primary-800">
              {t('route.scanresult.report.title')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              <strong className="text-gray-700">{websiteTitle}</strong>
              {scanDateLabel && (
                <> &middot; {t('route.scanresult.print.scanned_at')}: {scanDateLabel}</>
              )}
              {' '}&middot; {t('route.scanresult.report.subtitle_by')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            className="print:hidden inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-primary-root text-white hover:bg-primary-700 shrink-0"
          >
            <PrintIcon fontSize="small" />
            {t('route.scanresult.report.print_button')}
          </button>
        </div>

        {/* ── Loading / error / empty states ── */}
        {loading && !scanResult && (
          <div className="flex items-center justify-center py-16">
            <Spinner size="large" />
          </div>
        )}
        {error && !scanResult && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            {t('route.scanresult.report.load_error')}
          </div>
        )}
        {!loading && !scanResult && !error && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm">
            {t('route.scanresult.report.not_found')}
          </div>
        )}

        {scanResult && parsed && (
          <>
            {/* ── Score card ── */}
            <Card className="flex items-center gap-6 mb-3">
              {score && scoreStyle ? (
                <div className={`rounded-xl border-2 ${scoreStyle.border} ${scoreStyle.bg} flex flex-col items-center justify-center w-20 h-20 flex-shrink-0`}>
                  <span className={`text-4xl font-extrabold leading-none ${scoreStyle.text}`}>{score}</span>
                  <span className={`text-[11px] font-medium mt-0.5 ${scoreStyle.text}`}>
                    {t(`route.scanresult.score_label.${score.toLowerCase()}`, score)}
                  </span>
                </div>
              ) : (
                <div className="rounded-xl border-2 border-gray-200 bg-gray-50 flex items-center justify-center w-20 h-20 flex-shrink-0">
                  <span className="text-3xl font-bold text-gray-400">?</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className={`font-semibold text-base mb-1 ${scoreStyle?.text ?? 'text-gray-700'}`}>
                  {score?.toUpperCase() === 'A' &&
                   criticalItems.length === 0 &&
                   importantItems.length === 0 &&
                   recommendedItems.length === 0
                    ? t('route.scanresult.report.score_verdict.a_perfect')
                    : t(`route.scanresult.report.score_verdict.${score?.toLowerCase() ?? 'unknown'}`,
                        t('route.scanresult.report.score_verdict.unknown'))}
                </p>
                {/* Summary tiles */}
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <SummaryTile value={totalDeductions} label={t('route.scanresult.report.summary.total_deductions')} variant={totalDeductions > 0 ? 'error' : 'ok'} />
                  <SummaryTile value={cookiesNotInDse.length} label={t('route.scanresult.report.summary.cookies_missing')} variant={cookiesNotInDse.length > 0 ? 'error' : 'ok'} />
                  <SummaryTile value={tpsNotInDse.length} label={t('route.scanresult.report.summary.tps_missing')} variant={tpsNotInDse.length > 0 ? 'error' : 'ok'} />
                </div>
              </div>
            </Card>
            {/* Score explainer */}
            <p className="text-xs text-gray-400 mb-4 px-1">
              {t('route.scanresult.report.score_how')}
            </p>

            {/* Deduction breakdown (compact, same format as detail view) */}
            {!dseFound ? (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-5 text-xs">
                {dseMatches.site_unreachable ? (
                  <p className="text-red-700 font-medium">{t('route.scanresult.details.site_unreachable_notice')}</p>
                ) : (
                  <>
                    <p className="text-red-700 font-medium">{t('route.scanresult.details.no_dse_score_notice')}</p>
                    <p className="text-gray-500 mt-0.5">{t('route.scanresult.details.no_dse_www_tip')}</p>
                  </>
                )}
              </div>
            ) : totalDeductions > 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 mb-5">
                <p className="text-xs font-medium text-gray-500 mb-2">{t('route.scanresult.details.score_deduction_summary')}</p>
                <div className="flex flex-col gap-2 text-xs">
                  {cookiesNotInDse.length > 0 && (
                    <div>
                      <p className="text-red-600 font-medium mb-1">{t('route.scanresult.details.deduction_cookies_missing', { count: cookiesNotInDse.length })}</p>
                      <div className="flex flex-wrap gap-1">
                        {cookiesNotInDse.map((c) => (
                          <span key={c} className="bg-red-50 text-red-700 border border-red-200 rounded px-1.5 py-0.5 font-mono">{c}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {tpsNotInDse.length > 0 && (
                    <div>
                      <p className="text-red-600 font-medium mb-1">{t('route.scanresult.details.deduction_tps_missing', { count: tpsNotInDse.length })}</p>
                      <div className="flex flex-wrap gap-1">
                        {tpsNotInDse.map((d) => (
                          <span key={d} className="bg-red-50 text-red-700 border border-red-200 rounded px-1.5 py-0.5 font-mono">{d}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* ── Scan profile ── */}
            {scanProfile && (
              <div className="flex flex-wrap gap-2 mb-6 text-xs text-gray-500">
                {scanProfile.depth && (
                  <span className="bg-white border border-gray-200 rounded-full px-3 py-1">
                    {t('route.scanresult.details.scan_depth_label')}: {t(`route.scanresult.details.scan_depth_${scanProfile.depth}`)}
                  </span>
                )}
                {typeof scanProfile.scanned_pages === 'number' && (
                  <span className="bg-white border border-gray-200 rounded-full px-3 py-1">
                    {t('route.scanresult.details.scan_pages_label', { count: scanProfile.scanned_pages })}
                  </span>
                )}
                {scanProfile.dse_source && (
                  <span className="bg-white border border-gray-200 rounded-full px-3 py-1">
                    {t(`route.scanresult.details.scan_dse_source_${scanProfile.dse_source}`)}
                  </span>
                )}
              </div>
            )}

            {/* ── Zusammenfassung ── */}
            <SectionTitle>{t('route.scanresult.report.section.summary')}</SectionTitle>
            <Card className="mb-6">
              {(() => {
                const findings: string[] = [];
                if (!dseFound) {
                  findings.push(t('route.scanresult.report.summary_section.no_dse'));
                }
                if (cookiesBeforeConsent.length > 0) {
                  findings.push(t('route.scanresult.report.summary_section.cookies_before_consent', { count: cookiesBeforeConsent.length }));
                }
                if (tpsBeforeConsentUnique.length > 0) {
                  findings.push(t('route.scanresult.report.summary_section.tps_before_consent', { count: tpsBeforeConsentUnique.length }));
                }
                if (cookiesNotInDse.length > 0) {
                  findings.push(t('route.scanresult.report.summary_section.cookies_missing', { count: cookiesNotInDse.length }));
                }
                if (tpsNotInDse.length > 0) {
                  findings.push(t('route.scanresult.report.summary_section.tps_missing', { count: tpsNotInDse.length }));
                }
                const cspHdr = securityHeaders?.headers?.find((h) => h.name === 'Content-Security-Policy');
                if (cspHdr && cspHdr.rating === 'bad') {
                  findings.push(t('route.scanresult.report.summary_section.csp_missing'));
                } else if (cspHdr && cspHdr.rating === 'warn') {
                  findings.push(t('route.scanresult.report.summary_section.csp_warn'));
                }
                const otherBadHeaders = (securityHeaders?.headers ?? []).filter(
                  (h) => h.name !== 'Content-Security-Policy' && (h.rating === 'bad' || h.rating === 'warn'),
                );
                if (otherBadHeaders.length > 0) {
                  findings.push(t('route.scanresult.report.summary_section.other_headers_missing', { count: otherBadHeaders.length }));
                }

                if (findings.length === 0) {
                  return (
                    <p className="text-sm text-green-700">{t('route.scanresult.report.summary_section.intro_ok')}</p>
                  );
                }

                return (
                  <div>
                    {totalDeductions > 0 && (
                      <p className="text-xs text-gray-500 mb-3">
                        {t('route.scanresult.report.summary_section.score_deductions', { count: totalDeductions })}
                      </p>
                    )}
                    <ul className="space-y-2">
                      {findings.map((f, i) => (
                        <li key={i} className="flex gap-2 text-sm text-gray-700">
                          <span className="flex-shrink-0 mt-0.5 text-red-500">•</span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    {/* Cross-reference: the pre-consent third parties are always a
                        subset of the DSE-missing third parties (both require
                        "not in DSE"), so when both findings appear they describe
                        the same services from two angles — flag that overlap. */}
                    {tpsBeforeConsentUnique.length > 0 && tpsNotInDse.length > 0 && (
                      <p className="text-xs text-gray-500 mt-3 italic">
                        {t('route.scanresult.report.summary_section.tps_cross_reference')}
                      </p>
                    )}
                  </div>
                );
              })()}
            </Card>

            {/* ── Action plan ── */}
            <SectionTitle>{t('route.scanresult.report.section.action_plan')}</SectionTitle>
            <div className="flex flex-col gap-3 mb-6">
              <SeverityBlock
                severity="critical"
                title={t('route.scanresult.report.action_plan.critical_title')}
                description={t('route.scanresult.report.action_plan.critical_description')}
                items={criticalItems}
              />
              <SeverityBlock
                severity="important"
                title={t('route.scanresult.report.action_plan.important_title')}
                description={t('route.scanresult.report.action_plan.important_description')}
                items={importantItems}
              />
              <SeverityBlock
                severity="recommended"
                title={t('route.scanresult.report.action_plan.recommended_title')}
                description={t('route.scanresult.report.action_plan.recommended_description')}
                items={recommendedItems}
              />
              {criticalItems.length === 0 && importantItems.length === 0 && recommendedItems.length === 0 && (
                <Card>
                  <p className="text-sm text-green-700">{t('route.scanresult.details.no_score_deduction')}</p>
                </Card>
              )}
            </div>

            {/* ── Security headers ── */}
            {securityHeaders && (
              <>
                <SectionTitle>{t('route.scanresult.report.section.security_headers')}</SectionTitle>
                <p className="text-xs text-gray-500 mb-3 px-1">
                  {t('route.scanresult.report.security_headers.https_intro')}
                </p>
                <Card className="mb-6">
                  <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
                    <StatusBadge variant={securityHeaders.https ? 'ok' : 'error'}>
                      HTTPS {securityHeaders.https ? '✓' : '✗'}
                    </StatusBadge>
                    {securityHeaders.grade && (
                      <StatusBadge
                        variant={
                          securityHeaders.grade.toUpperCase() === 'A' || securityHeaders.grade.toUpperCase() === 'B'
                            ? 'ok'
                            : securityHeaders.grade.toUpperCase() === 'C'
                              ? 'warn'
                              : 'error'
                        }
                      >
                        {t('route.scanresult.report.security_headers.grade_label')}: {securityHeaders.grade.toUpperCase()}
                      </StatusBadge>
                    )}
                  </div>
                  <div className="flex flex-col gap-4">
                    {(securityHeaders.headers ?? []).map((hdr) => {
                      const isOk = hdr.rating === 'ok';
                      const isWarn = hdr.rating === 'warn';
                      const helpKey = SECURITY_HEADER_HELP_KEY[hdr.name];
                      const borderColor = isOk ? 'border-green-200' : isWarn ? 'border-amber-300' : 'border-red-200';
                      const bgColor = isOk ? 'bg-green-50' : isWarn ? 'bg-amber-50' : 'bg-red-50';
                      const Icon = isOk
                        ? <CheckCircleIcon fontSize="small" className="text-green-600 flex-shrink-0 mt-0.5" />
                        : isWarn
                          ? <WarningAmberIcon fontSize="small" className="text-amber-600 flex-shrink-0 mt-0.5" />
                          : <ErrorOutlineIcon fontSize="small" className="text-red-600 flex-shrink-0 mt-0.5" />;

                      return (
                        <div key={hdr.name} className={`rounded-lg border-2 ${borderColor} ${bgColor} p-3`}>
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            {Icon}
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="font-semibold text-sm text-gray-800">{hdr.name}</span>
                                <StatusBadge variant={isOk ? 'ok' : isWarn ? 'warn' : 'error'}>
                                  {isOk
                                    ? t('route.scanresult.details.security_ok')
                                    : isWarn
                                      ? t('route.scanresult.details.security_warn')
                                      : t('route.scanresult.details.security_bad')}
                                </StatusBadge>
                              </div>

                              {/* What is it — plain-language title + description */}
                              {helpKey && (
                                <div className="mb-2">
                                  <p className="text-xs font-semibold text-gray-700 mb-0.5">
                                    {t('route.scanresult.report.security_headers.what_it_means')}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {t(`route.scanresult.details.security_header_help.${helpKey}.description`)}
                                  </p>
                                </div>
                              )}

                              {/* Current value (monospace, compact) */}
                              {hdr.value && (
                                <p className="mb-2 text-[11px] font-mono bg-white rounded px-2 py-1 text-gray-500 break-all border border-gray-100">
                                  {hdr.value}
                                </p>
                              )}

                              {/* Notes from scan */}
                              {hdr.notes?.length > 0 && (
                                <ul className="mb-2 space-y-1">
                                  {hdr.notes.map((note, i) => (
                                    <li key={i} className="text-xs text-gray-600 flex gap-1.5">
                                      <span className="flex-shrink-0">•</span>
                                      <span>{translateScannerNote(note, i18n.resolvedLanguage || i18n.language)}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}

                              {/* Risk block — only for non-ok headers */}
                              {!isOk && helpKey && (
                                <div className="mt-2 pt-2 border-t border-gray-200 space-y-2">
                                  <div>
                                    <p className="text-xs font-semibold text-gray-700 mb-0.5">
                                      {t('route.scanresult.report.security_headers.risk_label')}
                                    </p>
                                    <p className="text-xs text-gray-600 leading-relaxed">
                                      {t(`route.scanresult.report.security_headers.risk.${helpKey}`)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs font-semibold text-gray-700 mb-0.5">
                                      {t('route.scanresult.report.security_headers.action_label')}
                                    </p>
                                    <p className="text-xs text-gray-600 leading-relaxed">
                                      {t(`route.scanresult.report.security_headers.action.${helpKey}`)}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </>
            )}

            {/* ── DSE Coverage ── */}
            <SectionTitle>{t('route.scanresult.report.section.dse_coverage')}</SectionTitle>

            {!dseFound ? (
              <Card className="mb-6">
                <p className="text-sm text-red-600">{t('route.scanresult.details.no_dse_found')}</p>
              </Card>
            ) : (
              <>
                <p className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                  {t('route.scanresult.report.dse_coverage.missing_explanation')}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  {/* Cookies table */}
                  <Card className="!p-0 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 bg-primary-50">
                      <h3 className="text-sm font-semibold text-primary-800">
                        {t('route.scanresult.report.dse_coverage.cookies_heading')}
                      </h3>
                    </div>
                    {allCookieNames.size === 0 ? (
                      <p className="px-4 py-3 text-xs text-gray-400 italic">{t('route.scanresult.report.dse_coverage.no_cookies')}</p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-100 bg-gray-50">
                            <th className="text-left px-3 py-2 font-medium text-gray-500">
                              {t('route.scanresult.report.dse_coverage.cookie')}
                            </th>
                            <th className="text-left px-3 py-2 font-medium text-gray-500">
                              {t('route.scanresult.report.dse_coverage.provider')}
                            </th>
                            <th className="text-right px-3 py-2 font-medium text-gray-500">
                              {t('route.scanresult.report.dse_coverage.status')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...allCookieNames].sort().map((name) => {
                            const inDse = dseCookieNames.has(name);
                            const generic = !inDse && genericCoveredNames.has(name);
                            const technical = technicallyNecessaryNames.has(name);
                            const provider = dseMatches.cookie_providers[name] ?? t('route.scanresult.report.dse_coverage.unknown_provider');
                            return (
                              <tr key={name} className={`border-b border-gray-50 last:border-0 ${!inDse && !generic && !technical ? 'bg-red-50/40' : ''}`}>
                                <td className="px-3 py-2 font-mono text-gray-700 truncate max-w-[120px]">{name}</td>
                                <td className="px-3 py-2 text-gray-500 truncate max-w-[100px]">{provider}</td>
                                <td className="px-3 py-2 text-right">
                                  {technical ? (
                                    <StatusBadge variant="neutral">{t('route.scanresult.report.dse_coverage.technically_necessary')}</StatusBadge>
                                  ) : (
                                    <StatusBadge variant={inDse ? 'ok' : generic ? 'neutral' : 'error'}>
                                      {inDse
                                        ? t('route.scanresult.report.dse_coverage.in_dse')
                                        : generic
                                          ? t('route.scanresult.report.dse_coverage.generically_covered')
                                          : t('route.scanresult.report.dse_coverage.missing_in_dse')}
                                    </StatusBadge>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </Card>

                  {/* Third parties table */}
                  <Card className="!p-0 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 bg-primary-50">
                      <h3 className="text-sm font-semibold text-primary-800">
                        {t('route.scanresult.report.dse_coverage.tps_heading')}
                      </h3>
                    </div>
                    {allTpDomains.size === 0 ? (
                      <p className="px-4 py-3 text-xs text-gray-400 italic">{t('route.scanresult.report.dse_coverage.no_tps')}</p>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-100 bg-gray-50">
                            <th className="text-left px-3 py-2 font-medium text-gray-500">
                              {t('route.scanresult.report.dse_coverage.domain')}
                            </th>
                            <th className="text-left px-3 py-2 font-medium text-gray-500">
                              {t('route.scanresult.report.dse_coverage.provider')}
                            </th>
                            <th className="text-right px-3 py-2 font-medium text-gray-500">
                              {t('route.scanresult.report.dse_coverage.status')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...allTpDomains].sort().map((domain) => {
                            const inDse = dseTpDomains.has(domain);
                            const isTechNecessary = technicallyNecessaryTpDomains.has(domain);
                            const provider = dseMatches.tp_providers[domain] ?? t('route.scanresult.report.dse_coverage.unknown_provider');
                            return (
                              <tr key={domain} className={`border-b border-gray-50 last:border-0 ${!inDse && !isTechNecessary ? 'bg-red-50/40' : ''}`}>
                                <td className="px-3 py-2 font-mono text-gray-700 truncate max-w-[120px]">{domain}</td>
                                <td className="px-3 py-2 text-gray-500 truncate max-w-[100px]">{provider}</td>
                                <td className="px-3 py-2 text-right">
                                  {isTechNecessary ? (
                                    <StatusBadge variant="neutral">{t('route.scanresult.report.dse_coverage.technically_necessary')}</StatusBadge>
                                  ) : (
                                    <StatusBadge variant={inDse ? 'ok' : 'error'}>
                                      {inDse
                                        ? t('route.scanresult.report.dse_coverage.in_dse')
                                        : t('route.scanresult.report.dse_coverage.missing_in_dse')}
                                    </StatusBadge>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </Card>
                </div>

                {/* Legend for badge meanings */}
                <div className="flex flex-col gap-1 mb-6 px-1">
                  <p className="text-[11px] text-gray-400">{t('route.scanresult.report.dse_coverage.generic_explanation')}</p>
                  <p className="text-[11px] text-gray-400">{t('route.scanresult.report.dse_coverage.technical_explanation')}</p>
                </div>
              </>
            )}

            {/* ── Consent states ── */}
            <SectionTitle>{t('route.scanresult.report.section.consent_states')}</SectionTitle>

            {/* Violation callout — combined cookies + third parties before consent */}
            {hasConsentViolations && (
              <Card className="mb-4 border-red-300 bg-red-50 !border-2">
                <div className="flex items-start gap-3">
                  <ErrorOutlineIcon className="text-red-600 flex-shrink-0 mt-0.5" fontSize="small" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-red-700 mb-2">
                      {t('route.scanresult.report.consent_states.violation_label')}
                    </p>
                    <p className="text-xs text-red-700 leading-relaxed mb-3">
                      {t('route.scanresult.report.consent_states.violation_explanation')}
                    </p>
                    {/* Two sub-groups: cookies + third parties */}
                    <div className="flex flex-col gap-3">
                      {cookiesBeforeConsent.length > 0 && (
                        <div>
                          <p className="text-[11px] font-semibold text-red-600 mb-1">
                            {t('route.scanresult.report.consent_states.cookies_before_label', { count: cookiesBeforeConsent.length })}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {cookiesBeforeConsent.map((c) => (
                              <span key={c.name} className="font-mono text-xs bg-red-100 text-red-800 border border-red-200 rounded px-2 py-0.5">
                                {c.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {tpsBeforeConsentUnique.length > 0 && (
                        <div>
                          <p className="text-[11px] font-semibold text-red-600 mb-1">
                            {t('route.scanresult.report.consent_states.tps_before_label', { count: tpsBeforeConsentUnique.length })}
                          </p>
                          <p className="text-[11px] text-red-600 mb-1.5">
                            {t('route.scanresult.report.consent_states.tps_before_explanation')}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {tpsBeforeConsentUnique.map((tp) => (
                              <span key={tp.domain} className="font-mono text-xs bg-red-100 text-red-800 border border-red-200 rounded px-2 py-0.5">
                                {tp.domain}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Consent state table — 3 columns, works in print */}
            {states && (
              <Card className="!p-0 overflow-hidden mb-6">
                {/* Pre-compute per-column status pills */}
                {(() => {
                  // pre_consent: problem if any non-technically-necessary cookie or any non-DSE, non-technically-necessary TP
                  const preConsentCookieViolation = states.pre_consent.cookies.some(
                    (c) => !technicallyNecessaryNames.has(c.name.toLowerCase()),
                  );
                  const preConsentTpViolation = (states.pre_consent.third_parties ?? []).some(
                    (tp) => !dseTpDomains.has((tp.domain || '').toLowerCase()) && !technicallyNecessaryTpDomains.has((tp.domain || '').toLowerCase()),
                  );
                  const preConsentBad = preConsentCookieViolation || preConsentTpViolation;

                  // post_reject: problem if any cookie that is NOT technically necessary
                  const postRejectCookieViolation = states.post_reject.cookies.some(
                    (c) => !technicallyNecessaryNames.has(c.name.toLowerCase()),
                  );

                  const pillFor = (key: 'pre_consent' | 'post_accept' | 'post_reject') => {
                    if (key === 'post_accept') return null;
                    const bad = key === 'pre_consent' ? preConsentBad : postRejectCookieViolation;
                    if (bad) {
                      return (
                        <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold bg-red-50 border border-red-200 text-red-700 rounded-full px-2 py-0.5">
                          <span>●</span>
                          {key === 'pre_consent'
                            ? t('route.scanresult.report.consent_states.pill_pre_bad')
                            : t('route.scanresult.report.consent_states.pill_reject_bad')}
                        </span>
                      );
                    }
                    return (
                      <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold bg-green-50 border border-green-200 text-green-700 rounded-full px-2 py-0.5">
                        <span>●</span>
                        {key === 'pre_consent'
                          ? t('route.scanresult.report.consent_states.pill_pre_ok')
                          : t('route.scanresult.report.consent_states.pill_reject_ok')}
                      </span>
                    );
                  };

                  return (
                    <>
                      {/* Column headers */}
                      <div className="grid grid-cols-3 border-b border-gray-200 bg-primary-50">
                        {(['pre_consent', 'post_accept', 'post_reject'] as const).map((key) => (
                          <div key={key} className={`px-3 py-2.5 ${key !== 'pre_consent' ? 'border-l border-gray-200' : ''}`}>
                            <p className="text-xs font-semibold text-primary-800">
                              {t(`route.scanresult.report.consent_states.${key}`)}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-0.5 leading-snug">
                              {t(`route.scanresult.report.consent_states.${key}_explanation`)}
                            </p>
                            <p className="text-xs font-medium text-primary-700 mt-1">
                              {t('route.scanresult.report.consent_states.cookies_count', { count: states[key].cookies.length })}
                            </p>
                            {pillFor(key)}
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
                {/* Cookie rows — zip the three lists together by index */}
                {(() => {
                  const maxLen = Math.max(
                    states.pre_consent.cookies.length,
                    states.post_accept.cookies.length,
                    states.post_reject.cookies.length,
                  );
                  if (maxLen === 0) return null;
                  const rows = Array.from({ length: maxLen }, (_, i) => ({
                    pre: states.pre_consent.cookies[i] ?? null,
                    accept: states.post_accept.cookies[i] ?? null,
                    reject: states.post_reject.cookies[i] ?? null,
                  }));
                  return rows.map((row, i) => (
                    <div
                      key={i}
                      className={`grid grid-cols-3 border-t border-gray-100 ${i % 2 === 1 ? 'bg-gray-50/50' : ''}`}
                    >
                      {(['pre', 'accept', 'reject'] as const).map((col, ci) => {
                        const cookie = row[col];
                        const isViolation = col === 'pre' && cookie != null && !technicallyNecessaryNames.has(cookie.name.toLowerCase());
                        return (
                          <div
                            key={col}
                            className={`px-3 py-1.5 text-xs font-mono ${ci !== 0 ? 'border-l border-gray-100' : ''} ${isViolation ? 'text-red-700' : 'text-gray-500'}`}
                          >
                            {cookie ? (
                              <span className="flex items-center gap-1 truncate">
                                <span className="truncate">{cookie.name}</span>
                                {isViolation && (
                                  <span className="flex-shrink-0 text-[10px] bg-red-50 border border-red-200 text-red-600 rounded px-1 leading-tight">!</span>
                                )}
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}

                {/* Third-party rows — deduplicated per state, zipped by index */}
                {(() => {
                  // Deduplicate by domain within each state (scanner emits one entry
                  // per domain×resource_type, same as the small view).
                  const dedupedTps = (['pre_consent', 'post_accept', 'post_reject'] as const).reduce(
                    (acc, stateKey) => {
                      const seen = new Map<string, string>();
                      for (const tp of (states[stateKey] as { third_parties?: { domain: string }[] }).third_parties ?? []) {
                        const d = (tp.domain || '').toLowerCase();
                        if (d && !seen.has(d)) seen.set(d, tp.domain);
                      }
                      acc[stateKey] = Array.from(seen.values());
                      return acc;
                    },
                    {} as Record<'pre_consent' | 'post_accept' | 'post_reject', string[]>,
                  );

                  const maxTpLen = Math.max(
                    dedupedTps.pre_consent.length,
                    dedupedTps.post_accept.length,
                    dedupedTps.post_reject.length,
                  );
                  if (maxTpLen === 0) return null;

                  return (
                    <>
                      {/* Sub-header: third-party counts */}
                      <div className="grid grid-cols-3 border-t-2 border-gray-200 bg-gray-50">
                        {(['pre_consent', 'post_accept', 'post_reject'] as const).map((stateKey, ci) => (
                          <div key={stateKey} className={`px-3 py-1.5 ${ci !== 0 ? 'border-l border-gray-200' : ''}`}>
                            <span className="text-[11px] font-semibold text-gray-500">
                              {t('route.scanresult.details.third_parties')}:{' '}
                              <span className="font-bold text-gray-700">{dedupedTps[stateKey].length}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                      {/* TP rows zipped by index */}
                      {Array.from({ length: maxTpLen }, (_, i) => {
                        const pre   = dedupedTps.pre_consent[i]   ?? null;
                        const acc   = dedupedTps.post_accept[i]  ?? null;
                        const rej   = dedupedTps.post_reject[i]  ?? null;
                        return (
                          <div key={`tp-${i}`} className={`grid grid-cols-3 border-t border-gray-100 ${i % 2 === 1 ? 'bg-gray-50/30' : ''}`}>
                            {([pre, acc, rej] as (string | null)[]).map((domain, ci) => {
                              const notInDse = domain != null && !dseTpDomains.has(domain.toLowerCase()) && !technicallyNecessaryTpDomains.has(domain.toLowerCase());
                              return (
                                <div
                                  key={ci}
                                  className={`px-3 py-1.5 text-xs font-mono ${ci !== 0 ? 'border-l border-gray-100' : ''} ${notInDse ? 'text-red-700' : 'text-gray-500'}`}
                                >
                                  {domain ? (
                                    <span className="flex items-center gap-1 truncate">
                                      <span className="truncate">{domain}</span>
                                      {notInDse && (
                                        <span className="flex-shrink-0 text-[10px] bg-red-50 border border-red-200 text-red-600 rounded px-1 leading-tight">
                                          {t('route.scanresult.details.missing_in_dse')}
                                        </span>
                                      )}
                                    </span>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
                {/* Footnote: caveat about technically-necessary classification */}
                <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/60">
                  <p className="text-[10px] text-gray-400 leading-snug">
                    {t('route.scanresult.report.consent_states.technically_necessary_caveat')}
                  </p>
                </div>
              </Card>
            )}

            {/* ── Footer ── */}
            <div className="border-t border-gray-200 mt-8 pt-4 text-center text-xs text-gray-400">
              {t('route.scanresult.report.footer', {
                date: scanDateLabel ?? new Date().toLocaleDateString(i18n.resolvedLanguage || i18n.language || undefined),
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Summary tile helper (defined after to keep top of file clean) ─────────────

function SummaryTile({
  value,
  label,
  variant,
}: {
  value: number;
  label: string;
  variant: 'ok' | 'warn' | 'error';
}) {
  const numColor = variant === 'ok' ? 'text-green-600' : variant === 'warn' ? 'text-orange-500' : 'text-red-600';
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-center">
      <div className={`text-2xl font-bold leading-none ${numColor}`}>{value}</div>
      <div className="text-[11px] text-gray-500 mt-1 leading-tight">{label}</div>
    </div>
  );
}
